"use client";

import { sendEmailAction } from "@/app/actions/sendEmailAction";
import { useCallback, useEffect, useState } from "react";
import { useUserData } from "@/app/[locale]/hooks/useUserData";
import { useFormState } from "react-dom";
import { SubmitBtn } from "@/app/[locale]/components/shared/SubmitBtn";
import clsx from "clsx";
import { event as gaEvent } from "@/app/lib/gtag";
import { contactFormSchema } from "@/app/formSchemas/expertContactFormSchema";
import { SUPPORT_EMAIL } from "@/app/constants";

const initialState = {
  error: null,
  success: false,
};

export default function ContactForm({
  guideEmail,
  guideName,
  text,
}: {
  guideEmail: string;
  guideName: string;
  text: any;
}) {
  const { data: user, isLoading, isError } = useUserData();
  const sendEmailActionWithEmail = sendEmailAction.bind(null, guideEmail);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formVisible, setFormVisible] = useState(true);
  const [touchedFields, setTouchedFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formState, formAction] = useFormState(
    sendEmailActionWithEmail,
    initialState,
  );

  // Validate a single field
  const validateField = (name: string, value: string) => {
    const result = contactFormSchema.safeParse({ ...formData, [name]: value });
    if (result.success === false) {
      const error = result.error.formErrors.fieldErrors[name]?.[0];
      setErrors((prev) => ({ ...prev, [name]: error || "" }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touchedFields[name]) {
      validateField(name, value);
    }
  };

  // Pre-fill form data if user is logged in
  useEffect(() => {
    if (
      user?.profile?.firstName &&
      user?.profile?.lastName &&
      !isLoading &&
      !isError
    ) {
      const fullName = `${user.profile.firstName} ${user.profile.lastName}`;
      setFormData((prev) => ({
        ...prev,
        name: fullName,
        email: user.email || "",
      }));
    }
  }, [user, isLoading, isError]);

  const isFormValid = contactFormSchema.safeParse(formData).success;

  useEffect(() => {
    if (formState.success) {
      gaEvent("Expert Contact Form Submitted", {
        event_category: "Expert Contact Form",
        event_label: `${guideEmail} Contact Form`,
        value: "1",
      });

      const timer = setTimeout(() => {
        setFormVisible(false);
        setShowForm(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [formState.success, guideEmail]);

  const toggleForm = useCallback(() => {
    gaEvent("Expert Contact Form Toggled", {
      event_category: "Expert Contact Form",
      event_label: `${guideEmail} Contact Form`,
      value: "0.5",
    });
    setShowForm(!showForm);
  }, [showForm, guideEmail]);

  return formVisible ? (
    <>
      <div>
        <button
          className={clsx("btn btn-secondary btn-lg mb-4", {
            "btn-outline": showForm,
          })}
          onClick={toggleForm}
        >
          {showForm ? text.cancel : text.writeMessage}
        </button>
      </div>
      <div
        className={clsx("collapse", {
          "collapse-open": showForm,
        })}
      >
        <div className="collapse-content p-0">
          {showForm && (
            <form action={formAction} className="space-y-4">
              <div className="flex justify-between space-x-4">
                <div className="w-full">
                  <label htmlFor="name" className="block font-bold">
                    {text.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={clsx("input input-bordered w-full", {
                      "input-error": touchedFields.name && errors.name,
                    })}
                    required
                  />
                  {touchedFields.name && errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>
                <div className="w-full">
                  <label htmlFor="email" className="block font-bold">
                    {text.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={clsx("input input-bordered w-full", {
                      "input-error": touchedFields.email && errors.email,
                    })}
                    required
                  />
                  {touchedFields.email && errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block font-bold">
                  {text.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={`Hi ${guideName}, ${text.example}`}
                  className={clsx("textarea textarea-bordered w-full", {
                    "textarea-error": touchedFields.message && errors.message,
                  })}
                  rows={5}
                  required
                ></textarea>
                {touchedFields.message && errors.message && (
                  <p className="mt-1 text-sm text-red-500">{errors.message}</p>
                )}
              </div>
              <SubmitBtn
                isFormValid={isFormValid}
                isSuccess={formState.success}
                isFullWidth
                btnColor="btn-primary"
              >
                {text.sendMessage}
              </SubmitBtn>
              {formState.error && (
                <p className="text-sm text-red-500">
                  {text.errorMessage}{" "}
                  <a className="link" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  ) : (
    <div className="border-2 p-2 text-center">
      <p className="text-lg font-bold">{text.messageSent} 🎉</p>
      <p className="text-sm">
        {text.responseExpected_1} {guideName} {text.responseExpected_2}
      </p>
    </div>
  );
}
